/*
 * NECCST · Judge.me reviews loader.
 * Pulls the official Judge.me widget HTML (public REST API, https://judge.me/api/docs)
 * and RE-RENDERS it into NECCST review cards (the comp design), rather than embedding
 * Judge.me's own markup. We only read text via textContent, so the output is XSS-safe.
 *
 * Why parse instead of embed: the public token can reach the widget endpoints (sanitized
 * HTML) but not the raw /reviews JSON (private). The widget HTML already carries every
 * field we need on each `.jdgm-rev` node (rating, author, date, body, photos, reply,
 * and data-product-title / .jdgm-rev__prod-link for the homepage "which product" remark).
 *
 * Container contract (sections/neccst-reviews.liquid):
 *   <div class="reviews-jdgm"
 *        data-jdgm-widget="product_review|all_reviews_page"
 *        data-jdgm-mode="product|store"
 *        data-jdgm-shop="store.myshopify.com"
 *        data-jdgm-token="PUBLIC_TOKEN"
 *        data-jdgm-handle="product-handle">   (handle for product_review only)
 */
(function () {
  var API = 'https://api.judge.me/api/v1/widgets/';

  function txt(node, sel) {
    var el = node.querySelector(sel);
    return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function initials(name) {
    var parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '·';
    var a = parts[0][0] || '';
    var b = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (a + b).toUpperCase();
  }

  // Privacy: keep the first name, abbreviate the surname to one letter. "Zhou Boyang" -> "Zhou B."
  function shortName(name) {
    var parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'Verified customer';
    if (parts.length === 1) return parts[0];
    return parts[0] + ' ' + parts[parts.length - 1][0].toUpperCase() + '.';
  }

  function whenLabel(ts) {
    if (!ts) return '';
    var iso = ts.replace(' UTC', 'Z').replace(' ', 'T');
    var d = new Date(iso);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('en', { month: 'short', year: 'numeric' });
  }

  function stars(score) {
    var r = Math.max(0, Math.min(5, Math.round(score || 0)));
    var s = '';
    for (var i = 0; i < 5; i++) s += i < r ? '★' : '☆';
    return s;
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  // Read one .jdgm-rev node into a plain object.
  function parseReview(rev) {
    var ratingEl = rev.querySelector('.jdgm-rev__rating');
    var score = ratingEl ? parseInt(ratingEl.getAttribute('data-score'), 10) : 0;
    var tsEl = rev.querySelector('.jdgm-rev__timestamp');
    var prod = rev.querySelector('.jdgm-rev__prod-link');
    var replyEl = rev.querySelector('.jdgm-rev__reply-content');

    var pics = [];
    var picNodes = rev.querySelectorAll('.jdgm-rev__pics img, .jdgm-rev__pic-link img');
    for (var i = 0; i < picNodes.length; i++) {
      var src = picNodes[i].getAttribute('data-src') || picNodes[i].getAttribute('src') || '';
      if (/^https?:/.test(src) || src.indexOf('//') === 0) pics.push(src);
    }

    var tsRaw = tsEl ? tsEl.getAttribute('data-content') : '';
    return {
      id: rev.getAttribute('data-review-id') || '',
      score: score,
      time: tsRaw ? (Date.parse(tsRaw.replace(' UTC', 'Z').replace(' ', 'T')) || 0) : 0,
      author: txt(rev, '.jdgm-rev__author'),
      title: txt(rev, '.jdgm-rev__title'),
      body: txt(rev, '.jdgm-rev__body'),
      when: whenLabel(tsRaw),
      verified: rev.getAttribute('data-verified-buyer') === 'true',
      productName: prod ? prod.textContent.replace(/\s+/g, ' ').trim() : (rev.getAttribute('data-product-title') || ''),
      productUrl: prod ? prod.getAttribute('href') : (rev.getAttribute('data-product-url') || ''),
      pics: pics,
      reply: replyEl ? replyEl.textContent.replace(/[ \t]+\n/g, '\n').trim() : ''
    };
  }

  // Build a NECCST review card from a parsed review.
  function renderCard(r, showProduct) {
    var card = el('article', 'review');

    var st = el('div', 'stars', stars(r.score));
    st.setAttribute('aria-label', (r.score || 0) + ' out of 5');
    card.appendChild(st);

    if (showProduct && r.productName) {
      var pl = el(r.productUrl ? 'a' : 'span', 'rev-prod', r.productName);
      if (r.productUrl) pl.setAttribute('href', r.productUrl.split('#')[0]);
      card.appendChild(pl);
    }

    if (r.body) card.appendChild(el('p', 'quote', r.body));

    if (r.pics.length) {
      var strip = el('div', 'review-photos');
      r.pics.slice(0, 4).forEach(function (src) {
        var wrap = el('div', 'rp');
        var img = el('img');
        img.src = src;
        img.loading = 'lazy';
        img.alt = 'Customer photo' + (r.productName ? ' of ' + r.productName : '');
        wrap.appendChild(img);
        strip.appendChild(wrap);
      });
      card.appendChild(strip);
    }

    var author = el('div', 'author');
    author.appendChild(el('div', 'avatar', initials(r.author)));
    var who = el('div', 'who');
    who.appendChild(el('span', 'name', shortName(r.author)));
    if (r.verified) {
      var v = el('span', 'verified');
      v.appendChild(el('span', 'chk', '✓'));
      v.appendChild(document.createTextNode('Verified buyer'));
      who.appendChild(v);
    }
    author.appendChild(who);
    if (r.when) author.appendChild(el('span', 'when', r.when));
    card.appendChild(author);

    if (r.reply) {
      var reply = el('div', 'review-reply');
      var rhead = el('div', 'rhead');
      var mark = el('span', 'rmark');
      mark.innerHTML = NECCST_MARK_SVG; // static, trusted brand SVG
      rhead.appendChild(mark);
      rhead.appendChild(el('span', 'rlabel', 'Response from NECCST'));
      reply.appendChild(rhead);
      reply.appendChild(el('p', null, r.reply));
      card.appendChild(reply);
    }

    return card;
  }

  var NECCST_MARK_SVG = "<svg viewBox='129 42 336 336' width='15' height='15' aria-hidden='true'><g transform='matrix(1,0,0,1,-612,-640)'><g transform='matrix(1.14,0,0,1.14,612.333,640.429)'><g transform='matrix(0.783941,0,0,0.783941,249.967,220.769)'><path fill='#204930' d='M13.645-20.041l-27.368-27.356l27.399-27.397l27.357,27.378L13.645-20.041z M77.792-47.416l27.248-27.24l-18.38-18.379L59.413-65.796L32.044-93.154l30.395-30.385l-18.38-18.379l-30.384,30.383l-30.385-30.383l-18.379,18.379l30.375,30.385l-27.399,27.378L-59.35-93.034l-18.39,18.379l27.259,27.258l-27.259,27.24l18.37,18.379l27.259-27.259l27.368,27.38l-30.375,30.364l18.369,18.38l30.384-30.365l30.375,30.365l18.379-18.359L32.015-1.658l27.4-27.38L86.661-1.779l18.38-18.379L77.792-47.416z M47.729,52.838c3.096,5.65,4.83,11.985,4.83,18.52c0,21.446-17.448,38.884-38.883,38.884c-21.436,0-38.874-17.438-38.874-38.884c0-6.515,1.724-12.828,4.791-18.461l-18.82-18.821c-7.606,10.764-11.975,23.752-11.975,37.282c0,35.776,29.102,64.878,64.878,64.878c35.775,0,64.878-29.102,64.878-64.878c0-13.548-4.379-26.557-12.005-37.34L47.729,52.838z M132.48-112.316c-15.103,0-29.494,5.432-40.887,14.732l18.518,18.54c6.525-4.631,14.292-7.275,22.368-7.275c21.434,0,38.872,17.456,38.872,38.883c0,21.446-17.438,38.884-38.872,38.884c-8.076,0-15.835-2.627-22.339-7.237L91.612,2.752c11.394,9.279,25.775,14.691,40.868,14.691c35.777,0.001,64.878-29.104,64.878-64.879C197.359-83.213,168.257-112.316,132.48-112.316 M-20.377-147.71c-3.096-5.653-4.821-11.986-4.821-18.52c0-21.446,17.437-38.883,38.874-38.883s38.883,17.437,38.883,38.883c0,6.535-1.744,12.867-4.83,18.52l18.82,18.82c7.626-10.783,12.005-23.79,12.005-37.34c0-35.776-29.103-64.878-64.878-64.878c-35.767,0-64.878,29.102-64.878,64.878c0,13.55,4.389,26.557,12.005,37.321L-20.377-147.71z M-105.128-8.553c-21.435,0-38.873-17.438-38.873-38.884c0-21.427,17.438-38.883,38.873-38.883c8.086,0,15.844,2.644,22.369,7.275l18.518-18.54c-11.395-9.3-25.785-14.732-40.887-14.732c-35.776,0-64.878,29.103-64.878,64.879c0,35.775,29.102,64.879,64.878,64.879c15.093,0,29.474-5.412,40.868-14.691l-18.499-18.52C-89.293-11.18-97.052-8.553-105.128-8.553'></path></g></g></g></svg>";

  // Free channel-1 SEO: inject aggregateRating into our server-rendered Product JSON-LD using
  // the average + count parsed from the free Judge.me widget. Googlebot renders JS and reads it.
  // Skips if the server already emitted aggregateRating (e.g. the reviews metafield is synced).
  function injectProductRating(avg, count) {
    if (!count || count < 1) return;
    var tag = document.getElementById('neccst-product-jsonld');
    if (!tag) return;
    try {
      var data = JSON.parse(tag.textContent);
      if (!data || data['@type'] !== 'Product' || data.aggregateRating) return;
      data.aggregateRating = {
        '@type': 'AggregateRating',
        'ratingValue': Math.round((avg || 0) * 10) / 10,
        'reviewCount': count,
        'bestRating': 5,
        'worstRating': 1
      };
      tag.textContent = JSON.stringify(data);
    } catch (e) {
      if (window.console) console.warn('[neccst] rating schema inject failed:', e && e.message);
    }
  }

  function setScore(section, avg, count) {
    var chip = section && section.querySelector('[data-jdgm-score]');
    if (!chip) return;
    if (!count) { chip.style.display = 'none'; return; }
    chip.innerHTML = '';
    chip.appendChild(el('div', 'stars', stars(avg)));
    var box = el('div');
    box.appendChild(el('div', 'n', (Math.round(avg * 10) / 10).toFixed(1) + ' / 5'));
    box.appendChild(el('small', null, count + (count === 1 ? ' review' : ' reviews')));
    chip.appendChild(box);
    chip.removeAttribute('hidden');
  }

  var MAX_PAGES = 60; // safety cap on lazy paging

  function load(el0) {
    var widget = el0.getAttribute('data-jdgm-widget');
    var shop = el0.getAttribute('data-jdgm-shop');
    var token = el0.getAttribute('data-jdgm-token');
    var handle = el0.getAttribute('data-jdgm-handle');
    var showProduct = el0.getAttribute('data-jdgm-mode') === 'store';
    var batch = parseInt(el0.getAttribute('data-jdgm-count'), 10); // reviews per lazy batch
    if (!batch || batch < 1) batch = 9;
    var moreLabel = el0.getAttribute('data-jdgm-more') || 'Read more';
    var lessLabel = el0.getAttribute('data-jdgm-less') || 'Read less';
    if (!widget || !shop || !token) return;

    function pageUrl(page) {
      var u = API + encodeURIComponent(widget) +
        '?api_token=' + encodeURIComponent(token) +
        '&shop_domain=' + encodeURIComponent(shop) +
        '&per_page=' + batch + '&page=' + page;
      if (widget === 'product_review' && handle) u += '&handle=' + encodeURIComponent(handle);
      return u;
    }

    var section = el0.closest('.reviews') || el0.parentNode;
    var seen = {};
    var page = 0, loading = false, done = false;
    var track, nav, prev, next;

    function step() {
      var c = track.querySelector('.review');
      return c ? c.getBoundingClientRect().width + 18 : track.clientWidth;
    }
    function updateNav() {
      if (!nav) return;
      var overflow = track.scrollWidth - track.clientWidth;
      nav.style.display = (overflow > 2 || !done) ? '' : 'none';
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = done && track.scrollLeft >= overflow - 2;
    }
    function maybeLoadMore() {
      if (done || loading) return;
      if (track.scrollLeft + track.clientWidth >= track.scrollWidth - step() * 1.5) fetchNext();
    }
    // Collapse a long review body to --rev-clamp lines; add a Read more toggle only if it overflows.
    function clampCard(card) {
      var q = card.querySelector('.quote');
      if (!q) return;
      q.classList.add('is-clamped');
      requestAnimationFrame(function () {
        if (q.scrollHeight - q.clientHeight <= 4) { q.classList.remove('is-clamped'); return; }
        var btn = el('button', 'rev-more', moreLabel);
        btn.type = 'button';
        btn.setAttribute('aria-expanded', 'false');
        btn.addEventListener('click', function () {
          var clamped = q.classList.toggle('is-clamped');
          btn.textContent = clamped ? moreLabel : lessLabel;
          btn.setAttribute('aria-expanded', clamped ? 'false' : 'true');
          if (typeof updateNav === 'function') updateNav();
        });
        q.parentNode.insertBefore(btn, q.nextSibling);
      });
    }
    // Re-order the loaded cards highest-rating-first (5-star always in front), newest first
    // as a tiebreak. Reviews arrive newest-first page by page, so a high-rated older review can
    // load after lower-rated newer ones; we sort on every append and pin the reader's current
    // card so the carousel never jumps when a card slots in ahead of the viewport.
    function sortTrack() {
      var kids = Array.prototype.slice.call(track.children);
      if (kids.length < 2) return;
      var sl = track.scrollLeft;
      var atFront = sl <= 2; // reader hasn't scrolled: keep the top-rated cards in view
      var anchor = null, anchorDelta = 0;
      if (!atFront) {
        for (var i = 0; i < kids.length; i++) {
          if (kids[i].offsetLeft + kids[i].offsetWidth > sl + 1) { // first card still (partly) in view
            anchor = kids[i];
            anchorDelta = kids[i].offsetLeft - sl;
            break;
          }
        }
      }
      kids.sort(function (a, b) {
        var d = (+b.dataset.score || 0) - (+a.dataset.score || 0); // higher rating first
        return d || ((+b.dataset.time || 0) - (+a.dataset.time || 0)); // then newest first
      });
      for (var j = 0; j < kids.length; j++) track.appendChild(kids[j]); // appendChild moves the node
      if (atFront) track.scrollLeft = 0;                        // stay on the 5-star cards
      else if (anchor) track.scrollLeft = anchor.offsetLeft - anchorDelta; // keep the reader's card put
    }
    function buildSlider() {
      track = el('div', 'reviews-track');
      track.setAttribute('role', 'list');
      var slider = el('div', 'reviews-slider');
      slider.appendChild(track);
      nav = el('div', 'reviews-nav');
      prev = el('button', 'rev-arrow', '‹');
      next = el('button', 'rev-arrow', '›');
      prev.type = next.type = 'button';
      prev.setAttribute('aria-label', 'Previous reviews');
      next.setAttribute('aria-label', 'Next reviews');
      nav.appendChild(prev);
      nav.appendChild(next);
      slider.appendChild(nav);
      el0.innerHTML = '';
      el0.appendChild(slider);
      prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      next.addEventListener('click', function () { maybeLoadMore(); track.scrollBy({ left: step(), behavior: 'smooth' }); });
      track.addEventListener('scroll', function () { updateNav(); maybeLoadMore(); }, { passive: true });
      window.addEventListener('resize', updateNav);
    }

    // Append a page of reviews (API order is newest-first), then sortTrack() re-orders the
    // whole track so 5-star reviews stay in front regardless of which page they arrived on.
    function append(data, isFirst) {
      var reviewsHtml = data.widget || data.all_reviews || data.featured_carousel || '';
      var doc = new DOMParser().parseFromString(reviewsHtml || '<i></i>', 'text/html');
      if (isFirst) {
        var headerHtml = data.all_reviews_header || '';
        var root = doc.querySelector('.jdgm-rev-widg');
        var hdoc = headerHtml ? new DOMParser().parseFromString(headerHtml, 'text/html') : null;
        var avgEl = (root || (hdoc && hdoc.querySelector('[data-average-rating]')));
        var avg = avgEl ? parseFloat(avgEl.getAttribute('data-average-rating')) : 0;
        var count = root ? parseInt(root.getAttribute('data-number-of-reviews'), 10) : 0;
        if (!count && hdoc) {
          var m = (hdoc.body.textContent || '').match(/Based on\s+(\d+)\s+review/i);
          if (m) count = parseInt(m[1], 10);
        }
        setScore(section, avg, count);
        if (!showProduct) injectProductRating(avg, count); // product page only (one product => one rating)
      }
      var nodes = doc.querySelectorAll('.jdgm-rev');
      var added = 0;
      for (var i = 0; i < nodes.length; i++) {
        var r = parseReview(nodes[i]);
        var key = r.id || (r.author + '|' + r.time + '|' + r.body.slice(0, 40));
        if (seen[key]) continue;
        seen[key] = 1;
        if (!r.body && !r.pics.length) continue;
        var card = renderCard(r, showProduct);
        card.setAttribute('role', 'listitem');
        card.dataset.score = r.score || 0;
        card.dataset.time = r.time || 0;
        track.appendChild(card);
        clampCard(card);
        added++;
      }
      if (added) sortTrack(); // keep the track ordered highest-rating-first after each batch
      if (nodes.length < batch || page >= MAX_PAGES) done = true; // short/last page
      return added;
    }

    function fetchNext() {
      if (loading || done) return Promise.resolve(0);
      loading = true;
      page += 1;
      var first = page === 1;
      return fetch(pageUrl(page), { headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('Judge.me ' + r.status); return r.json(); })
        .then(function (data) {
          if (first) buildSlider();
          var added = append(data, first);
          loading = false;
          if (first && !track.children.length) { el0.setAttribute('data-jdgm-state', 'empty'); done = true; return 0; }
          el0.setAttribute('data-jdgm-state', 'loaded');
          updateNav();
          // If the first batch doesn't fill the viewport, pull one more so the slider is usable.
          if (!done && track.scrollWidth <= track.clientWidth + 4) return fetchNext();
          if (first) el0.dispatchEvent(new CustomEvent('neccst:judgeme:loaded', { bubbles: true }));
          return added;
        });
    }

    el0.setAttribute('data-jdgm-state', 'loading');
    fetchNext().catch(function (err) {
      el0.setAttribute('data-jdgm-state', 'error');
      if (window.console) console.warn('[neccst] Judge.me load failed:', err && err.message);
    });
  }

  function init() {
    var nodes = document.querySelectorAll('[data-jdgm-widget]');
    for (var i = 0; i < nodes.length; i++) load(nodes[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
